import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { DetectionEventKind, FieldPosition, GpsGapKind } from '@groupe-reca/contracts'

// Stockage local de la tablette : UNE base par compte (aucun mélange sur une tablette partagée).

/** Au-delà, les plus anciens points sont écartés (≈ 7 jours de suivi continu à 30 s). */
export const maxQueuedPositions = 20_000

export interface QueuedPosition extends FieldPosition {
  missionId: string
}

export interface QueuedGap {
  id: string
  missionId: string
  kind: GpsGapKind
  startedAt: string
  endedAt: string | null
}

/** Entrée du journal de détection (lot O4) : un événement du moteur, éventuellement suivi d'une terminaison AUTO. */
export interface JournalEntry {
  /** Identifiant de l'événement, produit par la tablette; sert aussi de clé d'idempotence de la terminaison. */
  id: string
  missionId: string
  occurredAt: string
  /** Résidence concernée, ou `null` (événement de mission : GPS suspendu, pause). */
  itemId: string | null
  kind: DetectionEventKind
  engineVersion: string
  payload: Record<string, unknown>
  /** Terminaison `source: 'AUTO'` à envoyer une fois l'événement reçu par le serveur. */
  completeItemId: string | null
}

interface OperatorDb extends DBSchema {
  journal: { key: string; value: JournalEntry; indexes: { byOccurred: string } }
  positions: { key: string; value: QueuedPosition; indexes: { byCaptured: string } }
  gaps: { key: string; value: QueuedGap }
  keys: { key: string; value: { scope: string; key: string } }
  meta: { key: string; value: { name: string; value: number } }
}

export interface LocalStore {
  enqueuePosition: (position: QueuedPosition) => Promise<void>
  /** Les plus anciens d'abord. */
  pendingPositions: (limit: number) => Promise<QueuedPosition[]>
  countPositions: () => Promise<number>
  removePositions: (ids: string[]) => Promise<void>
  /** Points écartés faute de place depuis l'ouverture de la base. */
  droppedPositions: () => Promise<number>
  saveGap: (gap: QueuedGap) => Promise<void>
  pendingGaps: () => Promise<QueuedGap[]>
  removeGap: (id: string) => Promise<void>
  addJournal: (entries: JournalEntry[]) => Promise<void>
  /** Les plus anciens d'abord. */
  pendingJournal: (limit: number) => Promise<JournalEntry[]>
  countJournal: () => Promise<number>
  removeJournal: (ids: string[]) => Promise<void>
  /** Clé d'idempotence persistée (rechargement de page, résultat incertain). */
  getKey: (scope: string) => Promise<string | undefined>
  setKey: (scope: string, key: string) => Promise<void>
  clearKey: (scope: string) => Promise<void>
}

export function localDatabaseName(userId: string): string {
  return `reca-operator-v3:${userId}`
}

export function openLocalStore(userId: string): LocalStore {
  let database: Promise<IDBPDatabase<OperatorDb>> | null = null
  const db = () =>
    (database ??= openDB<OperatorDb>(localDatabaseName(userId), 2, {
      upgrade(upgrade, oldVersion) {
        if (oldVersion < 1) {
          upgrade.createObjectStore('positions', { keyPath: 'id' }).createIndex('byCaptured', 'capturedAt')
          upgrade.createObjectStore('gaps', { keyPath: 'id' })
          upgrade.createObjectStore('keys', { keyPath: 'scope' })
          upgrade.createObjectStore('meta', { keyPath: 'name' })
        }
        if (oldVersion < 2) upgrade.createObjectStore('journal', { keyPath: 'id' }).createIndex('byOccurred', 'occurredAt')
      },
    }))

  return {
    async enqueuePosition(position) {
      const handle = await db()
      const tx = handle.transaction(['positions', 'meta'], 'readwrite')
      await tx.objectStore('positions').put(position)
      const count = await tx.objectStore('positions').count()
      if (count > maxQueuedPositions) {
        let excess = count - maxQueuedPositions
        let cursor = await tx.objectStore('positions').index('byCaptured').openCursor()
        while (cursor && excess > 0) {
          await cursor.delete()
          excess -= 1
          cursor = await cursor.continue()
        }
        const meta = (await tx.objectStore('meta').get('dropped'))?.value ?? 0
        await tx.objectStore('meta').put({ name: 'dropped', value: meta + count - maxQueuedPositions })
      }
      await tx.done
    },
    async pendingPositions(limit) {
      const handle = await db()
      const result: QueuedPosition[] = []
      let cursor = await handle.transaction('positions').store.index('byCaptured').openCursor()
      while (cursor && result.length < limit) {
        result.push(cursor.value)
        cursor = await cursor.continue()
      }
      return result
    },
    async countPositions() {
      return (await db()).count('positions')
    },
    async removePositions(ids) {
      const tx = (await db()).transaction('positions', 'readwrite')
      await Promise.all(ids.map((id) => tx.store.delete(id)))
      await tx.done
    },
    async droppedPositions() {
      return (await (await db()).get('meta', 'dropped'))?.value ?? 0
    },
    async saveGap(gap) {
      await (await db()).put('gaps', gap)
    },
    async pendingGaps() {
      return (await db()).getAll('gaps')
    },
    async removeGap(id) {
      await (await db()).delete('gaps', id)
    },
    async addJournal(entries) {
      const tx = (await db()).transaction('journal', 'readwrite')
      await Promise.all(entries.map((entry) => tx.store.put(entry)))
      await tx.done
    },
    async pendingJournal(limit) {
      const result: JournalEntry[] = []
      let cursor = await (await db()).transaction('journal').store.index('byOccurred').openCursor()
      while (cursor && result.length < limit) {
        result.push(cursor.value)
        cursor = await cursor.continue()
      }
      return result
    },
    async countJournal() {
      return (await db()).count('journal')
    },
    async removeJournal(ids) {
      const tx = (await db()).transaction('journal', 'readwrite')
      await Promise.all(ids.map((id) => tx.store.delete(id)))
      await tx.done
    },
    async getKey(scope) {
      return (await (await db()).get('keys', scope))?.key
    },
    async setKey(scope, key) {
      await (await db()).put('keys', { scope, key })
    },
    async clearKey(scope) {
      await (await db()).delete('keys', scope)
    },
  }
}

/** Demande au navigateur de ne pas évincer les données locales; refus possible (non garanti). */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    return await navigator.storage.persist()
  } catch {
    return false
  }
}
